import { getItemAnon, setItemAnon, removeItemAnon } from '../utils/localstorage'; // Adjust the path as needed
import _ from 'lodash';
import { visit } from "graphql";
import vars from "./vars";

/**
 * Checks if the Virtual Tour mode is enabled, from local storage.
 * @returns {boolean} - Returns true if the Virtual Tour mode is enabled, false otherwise.
 */
const isVirtualTourEnabled = () => {
    return getItemAnon({ itemKey: "feature.Platform.BlueXP/VirtualTourMode" }) ?? false;
}

/**
 * Enables the Virtual Tour mode by setting a flag in local storage
 * and reloads the page to apply the changes.
 * @returns {void}
 */
const enableVirtualTourMode = ({ orgId, legacyId }) => {
    setItemAnon({ itemKey: "vtOrgId" }, orgId)
    setItemAnon({ itemKey: "vtLegacyId" }, legacyId)
    setItemAnon({ itemKey: "feature.Platform.BlueXP/VirtualTourMode" }, true);
    setItemAnon({ itemKey: "feature.demo-mode" }, true);

    window.location.reload();
}

/**
 * Disables the Virtual Tour mode by removing the flag from local storage
 * and reloads the page to apply the changes.
 * @returns {void}
 */
const disableVirtualTourMode = () => {
    removeItemAnon({ itemKey: "vtOrgId" })
    removeItemAnon({ itemKey: "vtLegacyId" })
    removeItemAnon({ itemKey: `feature.Platform.BlueXP/VirtualTourMode` });
    removeItemAnon({ itemKey: `feature.demo-mode` });
    window.location.reload()
}

/** 
 * Compiles a template string with the provided variables.
 * This function uses lodash's template engine to replace placeholders in the template
 * with actual values from the vars object.
 * The placeholders in the template should be in the format {{variableName}}.
 * * @param {string|Object} template - The template string or object to compile.
 * @param {Object} vars - The variables to replace in the template.
 * @returns {Object}
 * @throws {Error} - Throws an error if the template is not a string or an object.
*/
const compileTemplate = (template, additionalVars = {}) => {
    let compiledTemplate;
    try {
        // Handle {{}} delimiter for text in the template
        _.templateSettings.escape = /\{\{(.+?)\}\}/g;
        const templateStr = typeof template !== 'string' ? JSON.stringify(template) : template;
        let compiler = _.template(templateStr);
        const currentTimeStamp = new Date().getTime();
        const dataFolder = getDataFolder();
        compiledTemplate = compiler({ ...vars, ...additionalVars, dataFolder });

        // Handle <num> tags for numbers in the template
        _.templateSettings.escape = /"<num>(.+?)<\/num>"/g;
        compiler = _.template(compiledTemplate);
        compiledTemplate = compiler({ currentTimeStamp });
        return JSON.parse(compiledTemplate);
    } catch (error) {
        throw new Error('Failed to compile seed template');
    }
}

/**
 * Resolves the response path based on the current mode (SaaS or NCL).
 * If in NCL mode, rewrites 'data/platform/' paths to 'data/platform-ncl/'.
 * @param {string} responsePath - The original response path from config.
 * @returns {string} - The resolved response path for the current mode.
 */
const resolveResponsePath = (responsePath) => {
    if (!responsePath) return responsePath;
    if (isOfflineNCLMode() && responsePath.startsWith('data/platform/')) {
        return responsePath.replace('data/platform/', 'data/platform-ncl/');
    }
    return responsePath;
}

/** 
 * Parses intercepted API with query params and returns specific response.
 * @param {Object} stub - The stub object.
 * @param {Object} request - The request object.
 * @returns {Object}
*/
const handleRESTQueryFilter = (stub, req) => {
    const queries = req.queryParams?.filter || req.url;

    for (const key of Object.keys(stub.filters)) {
        let response;

        if (stub.filters[key].queryMatch) {
            const filterQueries = stub.filters[key].queryMatch;
            const filterQueriesSplited = filterQueries.split(",");

            if (stub.filters[key].queryLimit) {
                if (req.url.includes(`limit=${stub.filters[key].queryLimit}`)) {
                    if (filterQueriesSplited.every(element => queries.includes(element))) {
                        response = require('./' + resolveResponsePath(stub.filters[key].response));
                        return response;
                    }
                }
            } else {
                if (filterQueriesSplited.every(element => queries.includes(element))) {
                    response = require('./' + resolveResponsePath(stub.filters[key].response));
                    return response;
                }
            }
        } else {
            response = require('./' + resolveResponsePath(stub.filters[key].response));
            return response;
        }
    }
}

/**
 * Parses intercepted API payload and returns specific response.
 * Supports string contains checks (comma-separated tokens) and object partial matches.
 * @param {Object} stub - The stub object.
 * @param {Object} req - The request object.
 * @returns {Object|undefined}
 */
const handleRESTQueryPayload = (stub, req) => {
    const body = req?.requestBody;
    let parsedBody = body;

    if (typeof body === 'string') {
        try {
            parsedBody = JSON.parse(body);
        } catch (_e) {
            parsedBody = body;
        }
    }

    const bodyAsString = typeof parsedBody === 'string' ? parsedBody : JSON.stringify(parsedBody || {});

    for (const key of Object.keys(stub.payload || {})) {
        let response;
        const payloadRule = stub.payload[key];

        if (payloadRule?.payloadMatch) {
            let isMatch = false;

            if (typeof payloadRule.payloadMatch === 'string') {
                const payloadTokens = payloadRule.payloadMatch.split(',').map((token) => token.trim()).filter(Boolean);
                isMatch = payloadTokens.every((token) => bodyAsString.includes(token));
            } else if (Array.isArray(payloadRule.payloadMatch)) {
                isMatch = payloadRule.payloadMatch.every((token) => bodyAsString.includes(token));
            } else if (_.isPlainObject(payloadRule.payloadMatch)) {
                isMatch = _.isMatch(parsedBody, payloadRule.payloadMatch);
            }

            if (isMatch) {
                response = require('./' + resolveResponsePath(payloadRule.response));
                return response;
            }
        } else if (payloadRule?.response) {
            response = require('./' + resolveResponsePath(payloadRule.response));
            return response;
        }
    }
}

/** * Handles GraphQL query filters by extracting filter arguments from the parsed query.
 * It checks for specific filters and returns the corresponding stub response.
 * @param {Object} parsedQuery - The parsed GraphQL query object.
 * @param {string} operationName - The name of the GraphQL operation.
 * @param {Object} stubGraphqlResponses - The stub responses for GraphQL queries.
 * @returns {Object} - The filtered response based on the query.
 */
const handleGraphqlQueryFilter = (parsedQuery, operationName, stubGraphqlResponses) => {
    const filters = {};
    visit(parsedQuery, {
        Argument(node) {
            if (node?.name.value === 'filter') {
                node?.value.fields.forEach(field => {
                    if (field?.name?.value && field?.value?.value) {
                        filters[field.name.value] = field.value.value;
                    }
                });
            }
        }
    });

    if (filters) {
        const filterName = filters?.name ? _.kebabCase(filters.name) : '';
        switch (filterName) {
            case 'app-preferences':
                return stubGraphqlResponses[operationName + '-' + filterName];
            case 'splashscreen-v-1-dismissed':
                return stubGraphqlResponses[operationName + '-' + filterName];
            default:
                break;
        }
    }

    return {};
}


/**
 * Sleep function that returns a promise that resolves after a specified time.
 * @param {number} ms - The number of milliseconds to sleep.
 * @returns {Promise} - A promise that resolves after the specified time.
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Determines if the simulator should run in NCL mode.
 * This checks the REACT_APP_IS_NCL environment variable.
 * @returns {boolean} - Returns true if NCL mode is enabled, false otherwise.
 */
const isOfflineNCLMode = () => {
    return process.env.REACT_APP_IS_NCL === "true";
}

/**
 * Gets the appropriate data folder path based on the environment mode.
 * @returns {string} - Returns 'data/platform-ncl' for NCL mode, 'data/platform' for SaaS mode.
 */
const getDataFolder = () => {
    return isOfflineNCLMode() ? 'data/platform-ncl' : 'data/platform';
}

const getStubResponse = (stub) => {
    const dataFolder = getDataFolder();
    // Load from environment-specific folder without fallback
    return compileTemplate(require('./' + dataFolder + '/' + stub + '.json'));
}

export {
    isVirtualTourEnabled,
    enableVirtualTourMode,
    disableVirtualTourMode,
    compileTemplate,
    handleRESTQueryFilter,
    handleGraphqlQueryFilter,
    sleep,
    getStubResponse,
    handleRESTQueryPayload,
    isOfflineNCLMode,
    getDataFolder,
    resolveResponsePath
};